import { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
// Akhil comments - ProductReviewCard import commented out as reviews section is disabled
// import ProductReviewCard from "./ProductReviewCard";
import {
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Chip,
  Typography
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import HomeProductCard from "../../Home/HomeProductCard";
import { useDispatch, useSelector } from "react-redux";
import { findProductById, findSimilarProducts } from "../../../../Redux/Customers/Product/Action";
import { smartAddToCart } from "../../../../Redux/Customers/Cart/Action";
// Removed hardcoded gounsPage1 import - now fetching similar products dynamically from database
// import { gounsPage1 } from "../../../../Data/Gouns/gouns";
import {
  getBoxSizeDisplay,
  calculateBoxPrice,
  getDefaultBoxSize,
  BOX_WEIGHTS
} from "../../../../utils/boxPricing";

// Akhil comments - Reviews placeholder - commented out as reviews section is disabled
// const reviews = { href: "#", average: 4, totalCount: 117 };

// Helper function to convert text to CamelCase
const toCamelCase = (str) => {
  if (!str) return '';
  return str
    .toLowerCase()
    .split(/[\s_-]+/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export default function ProductDetails() {
  const [selectedWeight, setSelectedWeight] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [activeImage, setActiveImage] = useState(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { customersProduct } = useSelector((store) => store);
  const { productId } = useParams();
  const jwt = localStorage.getItem("jwt");

  // Calculate current box price based on selected weight
  const currentBoxPrice = useMemo(() => {
    if (!selectedWeight || !customersProduct.product) return 0;

    const weight = BOX_WEIGHTS[selectedWeight];
    if (!weight) return 0;

    // Use discounted price per pound for calculation
    const perPoundPrice = customersProduct.product.discountedPrice;
    return calculateBoxPrice(weight, perPoundPrice);
  }, [selectedWeight, customersProduct.product]);

  // Calculate total price for current quantity
  const totalPrice = useMemo(() => {
    return currentBoxPrice * quantity;
  }, [currentBoxPrice, quantity]);

  // Check out of stock status - handles null, undefined, 0, and negative
  const isOutOfStock = !customersProduct.product?.quantity || customersProduct.product?.quantity <= 0;
  const isInactive = customersProduct.product?.active === false;
  const BULK_LIMIT = 10;

  const handleQuantityChange = (delta) => {
    setQuantity(prev => {
      const newQty = prev + delta;
      if (newQty < 1) return 1;
      if (newQty > BULK_LIMIT) {
        alert("For bulk orders exceeding 10 boxes, please contact Siri Farms directly via our About Us page.");
        return BULK_LIMIT;
      }
      return newQty;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isInactive) {
      alert("This product is currently not available.");
      return;
    }

    if (isOutOfStock) {
      alert("This product is currently out of stock.");
      return;
    }

    if (!selectedWeight) {
      alert("Please select a box size");
      return;
    }

    if (quantity > BULK_LIMIT) {
      alert("For bulk orders exceeding 10 boxes, please contact Siri Farms directly via our About Us page.");
      return;
    }

    const data = { productId, size: selectedWeight, quantity };
    await dispatch(smartAddToCart({ data, jwt, product: customersProduct.product }));
    navigate("/cart");
  };

  // Reset state when navigating to a different product
  useEffect(() => {
    // Reset selectedWeight when product changes to avoid stale data
    setSelectedWeight("");
    setQuantity(1);
  }, [productId]);

  // Set default box size when product sizes are loaded
  useEffect(() => {
    if (customersProduct.product?.sizes && customersProduct.product.sizes.length > 0) {
      const defaultSize = getDefaultBoxSize(customersProduct.product.sizes);
      if (defaultSize) {
        setSelectedWeight(defaultSize.name);
      }
    }
  }, [customersProduct.product?.sizes]);

  useEffect(() => {
    // Scroll to top when navigating to a new product
    window.scrollTo(0, 0);

    const data = { productId: Number(productId), jwt };
    dispatch(findProductById(data));
    // Fetch similar products from database (10 items)
    dispatch(findSimilarProducts(productId, 10));
    // Akhil comments - Reviews fetching commented out as reviews section is disabled
    // dispatch(getAllReviews(productId));
  }, [productId, dispatch, jwt]);

  return (
    <div className="bg-white lg:px-20">
      {isInactive && (
        <div className="w-full bg-gray-800 text-white text-center py-3 text-sm font-semibold tracking-wide uppercase">
          This product is currently not available
        </div>
      )}
      <div className="pt-6">
        <nav aria-label="Breadcrumb">
          <ol
            className="mx-auto flex max-w-2xl items-center space-x-2 px-4 sm:px-6 lg:max-w-7xl lg:px-8"
          >
            {/* Dynamic breadcrumb based on product category - Now functional with navigation */}
            {/* Home Link */}
            <li>
              <div className="flex items-center">
                <Link to="/" className="mr-2 text-sm font-medium text-gray-900 hover:text-gray-600">
                  Home
                </Link>
                <svg width={16} height={20} viewBox="0 0 16 20" aria-hidden="true" className="h-5 w-4 text-gray-500">
                  <path d="M6 4L10 10L6 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </div>
            </li>
            {/* Top Level Category - Links to category/subcategory page */}
            {customersProduct.product?.category?.parentCategory?.parentCategory && (
              <li>
                <div className="flex items-center">
                  <Link
                    to={`/${customersProduct.product.category.parentCategory.parentCategory.name.toLowerCase().replace(/\s+/g, '_')}`}
                    className="mr-2 text-sm font-medium text-gray-900 hover:text-gray-600"
                  >
                    {toCamelCase(customersProduct.product.category.parentCategory.parentCategory.name)}
                  </Link>
                  <svg width={16} height={20} viewBox="0 0 16 20" aria-hidden="true" className="h-5 w-4 text-gray-500">
                    <path d="M6 4L10 10L6 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </div>
              </li>
            )}
            {/* Second Level Category */}
            {customersProduct.product?.category?.parentCategory && (
              <li>
                <div className="flex items-center">
                  <Link
                    to={customersProduct.product.category.parentCategory.parentCategory?.name
                      ? `/${customersProduct.product.category.parentCategory.parentCategory.name.toLowerCase().replace(/\s+/g, '_')}/${customersProduct.product.category.parentCategory.name.toLowerCase().replace(/\s+/g, '_')}`
                      : `/${customersProduct.product.category.parentCategory.name.toLowerCase().replace(/\s+/g, '_')}`
                    }
                    className="mr-2 text-sm font-medium text-gray-900 hover:text-gray-600"
                  >
                    {toCamelCase(customersProduct.product.category.parentCategory.name)}
                  </Link>
                  <svg width={16} height={20} viewBox="0 0 16 20" aria-hidden="true" className="h-5 w-4 text-gray-500">
                    <path d="M6 4L10 10L6 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </div>
              </li>
            )}
            {/* Current Category/Product */}
            <li className="text-sm">
              <span
                aria-current="page"
                className="font-medium text-gray-500"
              >
                {toCamelCase(customersProduct.product?.category?.name) || toCamelCase(customersProduct.product?.title)}
              </span>
            </li>
          </ol>
        </nav>

        {/* product details */}
        <section className="product-detail-grid grid grid-cols-1 gap-x-8 gap-y-10 px-4 pt-10">
          <style>{`@media (min-width: 1024px) { .product-detail-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; } }`}</style>
          {/* Image gallery */}
          <div className="flex flex-col items-center justify-start">
            <div className="w-full max-w-[28rem] flex items-center justify-center bg-gray-50 rounded-lg p-4">
              <img
                src={customersProduct.product?.imageUrl}
                alt={customersProduct.product?.title || 'Product image'}
                className="max-h-[400px] w-auto h-auto object-contain rounded-md"
                style={{ maxWidth: '100%' }}
              />
            </div>
          </div>

          {/* Product info */}
          <div className="lg:col-span-1 min-w-0 w-full px-4 pb-16 sm:px-6 lg:px-8 lg:pb-24">
            <div className="lg:col-span-2">
              <h1 className="text-lg lg:text-xl font-semibold tracking-tight text-gray-900  ">
                {customersProduct.product?.brand}
              </h1>
              <h1 className="text-lg lg:text-xl tracking-tight text-gray-900 opacity-60 pt-1">
                {customersProduct.product?.title}
              </h1>
            </div>

            {/* Options */}
            <div className="mt-4 lg:row-span-3 lg:mt-0">
              <h2 className="sr-only">Product information</h2>

              {/* Box-based pricing display */}
              <div className="space-y-4">
                {selectedWeight && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <Typography variant="h6" className="font-semibold text-gray-900">
                          {customersProduct.product?.discountPersent > 0 && (
                            <span className="opacity-50 line-through mr-2">
                              ${(calculateBoxPrice(BOX_WEIGHTS[selectedWeight], customersProduct.product?.price) * quantity).toFixed(2)}
                            </span>
                          )}
                          ${totalPrice.toFixed(2)}
                        </Typography>
                        <Typography variant="body2" className="text-gray-600">
                          {getBoxSizeDisplay(selectedWeight)} - Total Price
                        </Typography>
                      </div>
                      {customersProduct.product?.discountPersent > 0 && (
                        <Chip
                          label={`${customersProduct.product?.discountPersent}% Off`}
                          color="success"
                          size="small"
                        />
                      )}
                    </div>
                  </div>
                )}

                {/* Original per-pound pricing for reference - COMMENTED OUT */}
                {/* <div className="flex space-x-5 items-center text-lg lg:text-xl tracking-tight text-gray-900">
                  <p className="font-semibold opacity-60 text-sm">
                    Original: ${customersProduct.product?.discountedPrice} <span className="text-xs font-normal text-gray-500">per lb</span>
                  </p>
                  <p className="opacity-50 line-through text-sm">
                    ${customersProduct.product?.price}
                  </p>
                </div> */}
              </div>

              {/* Akhil comments - Reviews section - Commented out as per requirement */}
              {/*
              <div className="mt-6">
                <h3 className="sr-only">Reviews</h3>

                <div className="flex items-center space-x-3">
                  <Rating
                    name="read-only"
                    value={4.6}
                    precision={0.5}
                    readOnly
                  />

                  <p className="opacity-60 text-sm">42807 Ratings</p>
                  <p className="ml-3 text-sm font-medium text-indigo-600 hover:text-indigo-500">
                    {reviews.totalCount} reviews
                  </p>
                </div>
              </div>
              */}

              <form className="mt-10" onSubmit={handleSubmit}>
                {/* Weight Selection - Dynamic from database */}
                <div className="mt-6">
                  <FormControl fullWidth size="small">
                    <InputLabel id="weight-select-label">Product Weight</InputLabel>
                    <Select
                      labelId="weight-select-label"
                      id="weight-select"
                      value={selectedWeight}
                      label="Product Weight"
                      onChange={(e) => setSelectedWeight(e.target.value)}
                      sx={{ minWidth: 200 }}
                    >
                      {customersProduct.product?.sizes?.map((size, index) => (
                        <MenuItem key={index} value={size.name}>
                          {getBoxSizeDisplay(size.name)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>

                {/* Quantity Selector */}
                <div className="mt-6">
                  <h3 className="text-sm font-medium text-gray-900 mb-2">Quantity</h3>
                  <div className="flex items-center space-x-3">
                    <IconButton
                      onClick={() => handleQuantityChange(-1)}
                      disabled={quantity <= 1 || isOutOfStock}
                      color="primary"
                      size="small"
                    >
                      <RemoveCircleOutlineIcon />
                    </IconButton>
                    <span className="py-2 px-6 border rounded-md text-lg font-medium bg-gray-50">
                      {quantity}
                    </span>
                    <IconButton
                      onClick={() => handleQuantityChange(1)}
                      disabled={quantity >= BULK_LIMIT || isOutOfStock}
                      color="primary"
                      size="small"
                      title={quantity >= BULK_LIMIT ? "Bulk limit reached (10 boxes)" : ""}
                    >
                      <AddCircleOutlineIcon />
                    </IconButton>
                  </div>
                  {/* Bulk Order Prompt - visible when at limit */}
                  {quantity >= BULK_LIMIT && (
                    <Typography
                      variant="body2"
                      className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-md text-amber-800"
                    >
                      For bulk orders exceeding 10 boxes, please contact Siri Farms directly via our{' '}
                      <Link to="/about-us" className="font-semibold underline hover:text-amber-900">
                        About Us
                      </Link>{' '}
                      page.
                    </Typography>
                  )}
                </div>

                <Button
                  variant="contained"
                  type="submit"
                  disabled={isOutOfStock}
                  sx={{
                    padding: ".8rem 2rem",
                    marginTop: "2rem",
                    backgroundColor: isOutOfStock ? '#ccc' : '#4a7c39',
                    '&:hover': { backgroundColor: isOutOfStock ? '#ccc' : '#3d6830' }
                  }}
                >
                  {isOutOfStock ? 'Out of Stock' : 'Add To Cart'}
                </Button>
              </form>
            </div>

            <div className="py-10 lg:col-span-2 lg:col-start-1 lg:border-r lg:border-gray-200 lg:pb-16 lg:pr-8 lg:pt-6">
              {/* Description and details */}
              <div>
                <h3 className="sr-only">Description</h3>

                <div className="space-y-6">
                  <p className="text-base text-gray-900">
                    {customersProduct.product?.description}
                  </p>
                </div>
              </div>

              {/* Product Info - Brand and Category */}
              <div className="mt-10">
                <h3 className="text-sm font-medium text-gray-900">Product Information</h3>
                <div className="mt-4 space-y-2">
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">Brand:</span> {customersProduct.product?.brand || 'Siri Farms'}
                  </p>
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">Category:</span> {customersProduct.product?.category?.name || 'Fruits'}
                  </p>
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">Color/Variety:</span> {customersProduct.product?.color || 'Fresh'}
                  </p>
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">Stock:</span> {customersProduct.product?.quantity > 0 ? 'In Stock' : 'Out of Stock'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Akhil comments - rating and review section - Commented out as per requirement */}
        {/*
        <section className="">
          <h1 className="font-semibold text-lg pb-4">
            Recent Review & Ratings
          </h1>

          <div className="border p-5">
            <Grid container spacing={7}>
              <Grid item xs={7}>
                <div className="space-y-5">
                  {customersProduct.product?.reviews.map((item, i) => (
                    <ProductReviewCard item={item} />
                  ))}
                </div>
              </Grid>

              <Grid item xs={5}>
                <h1 className="text-xl font-semibold pb-1">Product Ratings</h1>
                <div className="flex items-center space-x-3 pb-10">
                  <Rating
                    name="read-only"
                    value={4.6}
                    precision={0.5}
                    readOnly
                  />

                  <p className="opacity-60">42807 Ratings</p>
                </div>
                <Box>
                  <Grid
                    container
                    justifyContent="center"
                    alignItems="center"
                    gap={2}
                  >
                    <Grid xs={2}>
                      <p className="p-0">Excellent</p>
                    </Grid>
                    <Grid xs={7}>
                      <LinearProgress
                        className=""
                        sx={{ bgcolor: "#d0d0d0", borderRadius: 4, height: 7 }}
                        variant="determinate"
                        value={40}
                        color="success"
                      />
                    </Grid>
                    <Grid xs={2}>
                      <p className="opacity-50 p-2">19259</p>
                    </Grid>
                  </Grid>
                </Box>
                <Box>
                  <Grid
                    container
                    justifyContent="center"
                    alignItems="center"
                    gap={2}
                  >
                    <Grid xs={2}>
                      <p className="p-0">Very Good</p>
                    </Grid>
                    <Grid xs={7}>
                      <LinearProgress
                        className=""
                        sx={{ bgcolor: "#d0d0d0", borderRadius: 4, height: 7 }}
                        variant="determinate"
                        value={30}
                        color="success"
                      />
                    </Grid>
                    <Grid xs={2}>
                      <p className="opacity-50 p-2">19259</p>
                    </Grid>
                  </Grid>
                </Box>
                <Box>
                  <Grid
                    container
                    justifyContent="center"
                    alignItems="center"
                    gap={2}
                  >
                    <Grid xs={2}>
                      <p className="p-0">Good</p>
                    </Grid>
                    <Grid xs={7}>
                      <LinearProgress
                        className="bg-[#885c0a]"
                        sx={{ bgcolor: "#d0d0d0", borderRadius: 4, height: 7 }}
                        variant="determinate"
                        value={25}
                        color="orange"
                      />
                    </Grid>
                    <Grid xs={2}>
                      <p className="opacity-50 p-2">19259</p>
                    </Grid>
                  </Grid>
                </Box>
                <Box>
                  <Grid
                    container
                    justifyContent="center"
                    alignItems="center"
                    gap={2}
                  >
                    <Grid xs={2}>
                      <p className="p-0">Average</p>
                    </Grid>
                    <Grid xs={7}>
                      <LinearProgress
                        className=""
                        sx={{
                          bgcolor: "#d0d0d0",
                          borderRadius: 4,
                          height: 7,
                          "& .MuiLinearProgress-bar": {
                            bgcolor: "#885c0a",
                          },
                        }}
                        variant="determinate"
                        value={21}
                        color="success"
                      />
                    </Grid>
                    <Grid xs={2}>
                      <p className="opacity-50 p-2">19259</p>
                    </Grid>
                  </Grid>
                </Box>
                <Box>
                  <Grid
                    container
                    justifyContent="center"
                    alignItems="center"
                    gap={2}
                  >
                    <Grid xs={2}>
                      <p className="p-0">Poor</p>
                    </Grid>
                    <Grid xs={7}>
                      <LinearProgress
                        className=""
                        sx={{ bgcolor: "#d0d0d0", borderRadius: 4, height: 7 }}
                        variant="determinate"
                        value={10}
                        color="error"
                      />
                    </Grid>
                    <Grid xs={2}>
                      <p className="opacity-50 p-2">19259</p>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          </div>
        </section>
        */}

        {/* Similar Products - Dynamic from database */}
        <section className="pt-10 pb-10">
          <h1 className="py-5 text-xl font-bold">Similar Products</h1>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {customersProduct.similarProducts && customersProduct.similarProducts.length > 0 ? (
              customersProduct.similarProducts.slice(0, 10).map((item) => (
                <HomeProductCard key={item.id} product={item} />
              ))
            ) : (
              <p className="text-gray-500 col-span-full">Loading similar products...</p>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
